/**
 * ChaptersPanel - Chapter selection UI for jumping to specific scenes
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStoryStore } from '../../store/storyStore';

interface ChaptersPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChaptersPanel: React.FC<ChaptersPanelProps> = ({ isOpen, onClose }) => {
  const availableScenes = useStoryStore((state) => state.availableScenes);
  const currentScene = useStoryStore((state) => state.currentScene);

  const handleSceneSelect = (sceneId: string) => {
    console.log('[ChaptersPanel] Selected scene:', sceneId);

    // Navigate to the selected scene
    if (window.NordicNeveInterop) {
      window.NordicNeveInterop.notifyNavigateToScene(sceneId);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/70 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Chapters Panel */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden pointer-events-auto"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gray-800 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Chapters</h2>
                <button
                  onClick={onClose}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)] sm:max-h-[calc(80vh-120px)]">
                {availableScenes.length === 0 ? (
                  <div className="text-center text-white/50 py-8">
                    <p>No chapters available</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableScenes.map((scene, index) => {
                      const isCurrentScene = currentScene?.id === scene.id;

                      return (
                        <button
                          key={scene.id}
                          onClick={() => handleSceneSelect(scene.id)}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                            isCurrentScene
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-800 text-white hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              isCurrentScene
                                ? 'bg-white text-blue-600'
                                : 'bg-gray-700 text-white'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold mb-1">{scene.title}</h3>
                              {scene.description && (
                                <p className="text-sm text-white/70 line-clamp-2">
                                  {scene.description}
                                </p>
                              )}
                              {isCurrentScene && (
                                <p className="text-xs text-white/90 mt-1 font-medium">
                                  ● Currently playing
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-800 px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-700 flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 sm:px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChaptersPanel;
